import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { REQUEST_TYPES } from '@demandproai/shared';
import { EASY_MODE_STEPS, EASY_MODE_STEP_LABELS } from '../constants/easyModeSteps';
import { buildVirtualUserPayload } from '../utils/easyModePayload';
import { toaster } from '../utils/toast';
import useDemandCreditCheck from './useDemandCreditCheck';
import useEmbedded from './useEmbedded';
import usePfqAxios from './usePfqAxios';

const VIRTUAL_USER_ENDPOINT = '/virtual-user';

const PREREQUISITE_STEPS = [EASY_MODE_STEPS.CASE_INTAKE, EASY_MODE_STEPS.DOCUMENT_UPLOAD];

const useEasyModeSubmit = ({
  caseId,
  intakeValues,
  additionalContext,
  saveIntake,
  saveAdditionalContext,
  saveDefendant,
  stepValidation,
  createRequests
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  // straight to the PFQ with the user's JWT, which is where userId and dbName come from
  const { postData } = usePfqAxios();
  const { isEmbedded, sendMessageToParent } = useEmbedded();
  const { confirmDemandCredit } = useDemandCreditCheck();

  // a case that has already been through the assistant is regenerated rather than generated: a resubmit
  // restarts the run from scratch, superseding whatever is in flight, instead of being blocked by it
  const hasExistingRun = Boolean(createRequests?.[REQUEST_TYPES.VIRTUAL_USER]);

  const blockingStep = PREREQUISITE_STEPS.find((step) => !stepValidation[step].isValid);

  const submitTooltip = blockingStep
    ? `Finish ${EASY_MODE_STEP_LABELS[blockingStep]} before generating the demand`
    : '';

  const submit = async () => {
    // a run in flight is deliberately not a blocker here: a second click restarts the assistant
    if (isSubmitting || blockingStep) return;

    setIsSubmitting(true);

    try {
      // only the very first generation prompts for a credit; a restart neither re-prompts nor spends one -
      // the PFQ charges per demand produced and the same plaintiff count is already paid for
      if (!hasExistingRun && !(await confirmDemandCredit({ plaintiffCount: intakeValues.plaintiffCount }))) return;

      // persist every edited surface before the run reads the case, so the demand captures the changes
      // and the payload matches what the PFQ validates against
      if (!(await saveIntake())) return;
      await Promise.all([saveAdditionalContext(), saveDefendant()]);

      // always re-run the assistant; a restart supersedes the in-progress run and regenerates the letter
      // useAxios resolves rather than throws on a 4xx, so the failure has to be read off the body
      const response = await postData(
        VIRTUAL_USER_ENDPOINT,
        buildVirtualUserPayload({ caseId, intakeValues, additionalContext, forceRegenerate: hasExistingRun })
      );

      if (response?.success === false) {
        throw new Error(response.message);
      }

      toaster({ message: 'Your demand is being generated', success: true });

      if (isEmbedded) {
        sendMessageToParent({ type: 'easyModeSubmitted', caseId });
        return;
      }

      navigate('/cases');
    } catch (error) {
      toaster({ message: error?.message ?? 'Could not start the demand', success: false });
    } finally {
      setIsSubmitting(false);
    }
  };

  return { isSubmitting, isSubmitDisabled: Boolean(blockingStep), submitTooltip, submit, hasExistingRun };
};

export default useEasyModeSubmit;
